import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { toast } from "sonner"
import { Group, Panel, Separator } from "react-resizable-panels"
import { SquareDashedMousePointer } from "lucide-react"
import { EmptyState } from "@ui/EmptyState"
import { apiFetch } from "@lib/api"
import type {
  ApiRequest,
  Credential,
  Environment,
  Folder,
  Project,
  RequestExample,
  RequestRun,
} from "@lib/inertia-types"
import { RequestTree } from "@components/workspace/RequestTree"
import { RequestBuilder } from "@components/workspace/RequestBuilder"
import { FolderSettingsPanel, type HttpSettingRow } from "@components/workspace/FolderSettings"

type CredentialSummary = Pick<Credential, "id" | "name" | "kind">

// Below md the workspace is a single-panel master/detail: the tree shows
// full-width when nothing is selected (where the "Requests" breadcrumb lands),
// the builder full-width once a request is picked.
function useIsDesktop() {
  const query = "(min-width: 768px)"
  const [isDesktop, setIsDesktop] = React.useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : true,
  )
  React.useEffect(() => {
    const mq = window.matchMedia(query)
    const apply = () => setIsDesktop(mq.matches)
    apply()
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [])
  return isDesktop
}

export default function Workspace({
  project,
  folders,
  requests,
  selected_request,
  selected_example = null,
  environments = [],
  credentials = [],
  folder_settings = [],
  host = "",
}: {
  project: Project
  folders: Folder[]
  requests: ApiRequest[]
  selected_request: ApiRequest | null
  selected_example?: RequestExample | null
  environments?: Environment[]
  credentials?: CredentialSummary[]
  folder_settings?: HttpSettingRow[]
  host?: string
}) {
  const [settingsFolder, setSettingsFolder] = React.useState<Folder | null>(null)
  const isDesktop = useIsDesktop()

  const folderById = React.useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders])

  const serviceIdOf = React.useCallback(
    (folderId: number | null): number | null => {
      let node = folderId ? folderById.get(folderId) : undefined
      while (node && node.parent_id) node = folderById.get(node.parent_id)
      return node?.id ?? null
    },
    [folderById],
  )

  const selectedServiceId = selected_request ? serviceIdOf(selected_request.folder_id) : null
  const selectedServiceSlug = selectedServiceId ? folderById.get(selectedServiceId)?.slug ?? null : null

  // Guard against a stale example prop that belongs to a different request.
  const selectedExample =
    selected_example && selected_request && selected_example.api_request_id === selected_request.id
      ? selected_example
      : null
  const selectedExampleId = selectedExample?.id ?? null

  // Environments applicable to the selected request: its service's set plus
  // project-wide shared sets.
  const applicableEnvs = React.useMemo(
    () => environments.filter((e) => e.folder_id === null || e.folder_id === selectedServiceId),
    [environments, selectedServiceId],
  )

  // Environment selection persists per user+service in localStorage.
  const storageKey = `fetch-env:${project.slug}:${selectedServiceId ?? "root"}`
  const [environmentId, setEnvironmentId] = React.useState<number | null>(null)

  React.useEffect(() => {
    const stored = localStorage.getItem(storageKey)
    const id = stored ? Number(stored) : null
    setEnvironmentId(id && applicableEnvs.some((e) => e.id === id) ? id : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, applicableEnvs.map((e) => e.id).join(",")])

  // The environment is a saved attribute of an example; for the plain request
  // (scratchpad) it stays a per-user localStorage preference.
  const activeEnvironmentId = selectedExample ? selectedExample.environment_id : environmentId

  function changeEnvironment(id: number | null) {
    if (selectedExample && selected_request) {
      router.patch(
        `/projects/${project.slug}/requests/${selected_request.id}/examples/${selectedExample.id}`,
        { environment_id: id } as unknown as Record<string, never>,
        { preserveScroll: true, preserveState: true, only: ["selected_example"] },
      )
      return
    }
    setEnvironmentId(id)
    if (id) localStorage.setItem(storageKey, String(id))
    else localStorage.removeItem(storageKey)
  }

  // ── Execution ───────────────────────────────────────────────────
  const [run, setRun] = React.useState<RequestRun | null>(null)
  const [running, setRunning] = React.useState(false)
  const [history, setHistory] = React.useState<RequestRun[]>([])

  // Reset the response pane when switching requests, and load recent runs.
  React.useEffect(() => {
    setRun(null)
    setHistory([])
    if (!selected_request) return
    let cancelled = false
    apiFetch<RequestRun[]>(`/projects/${project.slug}/requests/${selected_request.id}/runs`)
      .then((runs) => {
        if (cancelled) return
        setHistory(runs)
        if (runs.length > 0) setRun(runs[0])
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [project.slug, selected_request?.id])

  async function sendRequest() {
    if (!selected_request) return
    setRunning(true)
    try {
      const params = new URLSearchParams()
      if (activeEnvironmentId) params.set("environment_id", String(activeEnvironmentId))
      if (selectedExampleId) params.set("example_id", String(selectedExampleId))
      const query = params.toString() ? `?${params.toString()}` : ""
      const result = await apiFetch<RequestRun>(
        `/projects/${project.slug}/requests/${selected_request.id}/runs${query}`,
        { method: "POST" },
      )
      setRun(result)
      setHistory((prev) => [result, ...prev].slice(0, 20))
      if (result.status === "blocked" || result.status === "error") {
        toast.error(result.error_message ?? "Request failed")
      }
    } catch {
      toast.error("Could not execute the request")
    } finally {
      setRunning(false)
    }
  }

  const settingsFolderEnvs = React.useMemo(() => {
    if (!settingsFolder) return []
    const serviceId = serviceIdOf(settingsFolder.id)
    return environments.filter((e) => e.folder_id === null || e.folder_id === serviceId)
  }, [settingsFolder, environments, serviceIdOf])

  const tree = (
    <div className="h-full border-cloverhound-gray-200 md:border-r dark:border-cloverhound-gray-850">
      <RequestTree
        project={project}
        folders={folders}
        requests={requests}
        selectedRequestId={settingsFolder ? null : selected_request?.id ?? null}
        selectedExampleId={settingsFolder ? null : selectedExampleId}
        settingsFolderId={settingsFolder?.id ?? null}
        onFolderSettings={setSettingsFolder}
        onRequestSelected={() => setSettingsFolder(null)}
      />
    </div>
  )

  const detail = settingsFolder ? (
    <FolderSettingsPanel
      key={settingsFolder.id}
      project={project}
      folder={settingsFolder}
      environments={settingsFolderEnvs}
      settings={folder_settings.filter((s) => s.owner_id === settingsFolder.id)}
      credentials={credentials}
      onClose={() => setSettingsFolder(null)}
    />
  ) : selected_request ? (
    <RequestBuilder
      key={`${selected_request.id}:${selectedExampleId ?? "scratch"}`}
      project={project}
      request={selected_request}
      example={selectedExample}
      environments={applicableEnvs}
      credentials={credentials}
      environmentId={activeEnvironmentId}
      serviceSlug={selectedServiceSlug}
      mockToken={project.mock_token}
      host={host}
      onEnvironmentChange={changeEnvironment}
      onSend={sendRequest}
      running={running}
      run={run}
      history={history}
      onSelectRun={setRun}
    />
  ) : (
    <div className="grid h-full place-items-center">
      <EmptyState
        icon={<SquareDashedMousePointer className="size-6" aria-hidden="true" />}
        title="Select a request"
        description="Pick a request from the tree, or create a service folder to group requests for one API."
      />
    </div>
  )

  return (
    <>
      <Head title={`${project.name} · Fetch`} />
      {isDesktop ? (
        <Group orientation="horizontal" className="h-full">
          <Panel defaultSize="22" minSize="14" maxSize="40">
            {tree}
          </Panel>
          <Separator className="w-1 bg-transparent transition-colors hover:bg-cloverhound-indigo-400/50 data-[dragging]:bg-cloverhound-indigo-400" />
          <Panel minSize="30">{detail}</Panel>
        </Group>
      ) : (
        // Mobile master/detail: the tree hides entirely once a request (or
        // folder settings) is active; tapping the "Requests" breadcrumb returns
        // to the project root, which brings the full-width tree back.
        <div className="h-full">
          {selected_request || settingsFolder ? detail : tree}
        </div>
      )}
    </>
  )
}
